import { useState, useEffect, useCallback } from 'react';
import { useActiveStore } from '../context/ActiveStoreContext';
import { PERMISSION_CONFIG, UserPermission } from './usePermissionCheck';
import axios from '../lib/axios';

interface UsePermissionsReturn {
  permissions: UserPermission;
  isLoading: boolean;
  error: string | null;
  checkPermission: (permission: keyof UserPermission) => boolean;
  hasAnyPermission: (permissions: (keyof UserPermission)[]) => boolean;
  hasAllPermissions: (permissions: (keyof UserPermission)[]) => boolean;
  refreshPermissions: () => Promise<void>;
}

const ALL_PERMISSIONS_FALSE: UserPermission = {
  sight_access: false,
  billing_access: false,
  settings_access: false,
  integrations_access: false,
  scaleboard_access: false,
  support_access: false,
  isight_access: false,
  notification_access: false,
};

const ALL_PERMISSIONS_TRUE: UserPermission = {
  sight_access: true,
  billing_access: true,
  settings_access: true,
  integrations_access: true,
  scaleboard_access: true,
  support_access: true,
  isight_access: true,
  notification_access: true,
};

export function useEnhancedPermissions(): UsePermissionsReturn {
  const { activeStore, isLoading: storesLoading } = useActiveStore();
  const [permissions, setPermissions] = useState<UserPermission>(ALL_PERMISSIONS_FALSE);
  // Start in the loading state whenever a token exists. Pages decide whether to
  // redirect during the FIRST render (before effects run), so starting with
  // isLoading=false would let them read the all-false defaults and bounce
  // straight to /profile on any entry where the store hasn't resolved yet.
  const [isLoading, setIsLoading] = useState<boolean>(() =>
    typeof window !== 'undefined' && !!localStorage.getItem('token'),
  );
  const [error, setError] = useState<string | null>(null);

  const loadPermissions = useCallback(async (): Promise<void> => {
    // console.log('🔍 loadPermissions called, activeStore:', activeStore);
    // console.log('🔍 activeStore.permissions:', activeStore?.permissions);

    if (!activeStore || storesLoading) {
      if (!activeStore && !storesLoading) {
        // Store resolution has finished and there is genuinely no active store.
        setPermissions(ALL_PERMISSIONS_FALSE);
        setIsLoading(false);
        return;
      }
      // Still resolving:
      //  - no active store yet while the store list is loading (refreshStores
      //    in flight), OR
      //  - a possibly-stale hydrated activeStore (e.g. cached with empty/all-false
      //    permissions from a previous failed session or invitation) that has not
      //    been reconciled with /api/stores yet.
      // In both cases we must NOT trust cached permissions: when we let
      // isLoading=false with unresolved/untracked state on direct/embedded
      // entries, pages treat the empty permissions as a definitive denial and
      // redirect to /profile even though the user has access.
      setIsLoading(true);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Owner/admin always have full access - grant every scope regardless of
      // what the backend currently has stored. This mirrors usePermissionCheck
      // and PermissionContext, and MUST be checked before the stored
      // permissions below so a stale/empty permissions object can never clip
      // a store owner of their scopes.
      if (activeStore.user_role === 'owner' || activeStore.user_role === 'admin') {
        setPermissions(ALL_PERMISSIONS_TRUE);
        return;
      }

      // First, check if permissions are already in the activeStore data
      if (activeStore.permissions) {
        // console.log('✅ Permissions found in activeStore:', activeStore.permissions);
        const parsedPermissions: UserPermission = {
          sight_access: activeStore.permissions['sight_access'] ?? false,
          billing_access: activeStore.permissions['billing_access'] ?? false,
          settings_access: activeStore.permissions['settings_access'] ?? false,
          integrations_access: activeStore.permissions['integrations_access'] ?? false,
          scaleboard_access: activeStore.permissions['scaleboard_access'] ?? false,
          support_access: activeStore.permissions['support_access'] ?? false,
          isight_access: activeStore.permissions['isight_access'] ?? false,
          notification_access: activeStore.permissions['notification_access'] ?? false,
        };
        // console.log('🔍 Final permissions set from store:', parsedPermissions);
        setPermissions(parsedPermissions);
        return;
      }

      const token = localStorage.getItem('token');
      // console.log('🔍 Token found:', !!token);
      if (!token) throw new Error('No authentication token found');

      // axios baseURL is already "/api" — do not prefix the path with /api again.
      const apiUrl = `/permissions/stores/${activeStore.id}`;
      // console.log('🌐 Fetching permissions from API:', apiUrl);

      const response = await axios.get(apiUrl);
      const data = response.data;
      // console.log('🔍 API Response:', data);

      if (!data.success) {
        throw new Error(data.message || 'Failed to load permissions');
      }

      // Parse permissions from JSON
      const permissionData = data.permissions ? JSON.parse(data.permissions) : {};
      // console.log('🔍 Parsed permission data:', permissionData);

      // Grant all permissions to owner/admin users (mirror usePermissionCheck)
      if (data.user_role === 'owner' || data.user_role === 'admin') {
        setPermissions(ALL_PERMISSIONS_TRUE);
        return;
      }

      const parsedPermissions: UserPermission = {
        sight_access: permissionData['sight_access'] ?? false,
        billing_access: permissionData['billing_access'] ?? false,
        settings_access: permissionData['settings_access'] ?? false,
        integrations_access: permissionData['integrations_access'] ?? false,
        scaleboard_access: permissionData['scaleboard_access'] ?? false,
        support_access: permissionData['support_access'] ?? false,
        isight_access: permissionData['isight_access'] ?? false,
        notification_access: permissionData['notification_access'] ?? false,
      };
      
      // console.log('🔍 Permission API Response Analysis:');
      // console.log('  - Raw permissionData:', permissionData);
      // console.log('  - Parsed permissions:', parsedPermissions);
      // console.log('  - Data type:', typeof permissionData);
      // console.log('  - Data keys:', Object.keys(permissionData));
      
      setPermissions(parsedPermissions);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load permissions');
      console.error('❌ Failed to load permissions:', err);
    } finally {
      setIsLoading(false);
    }
  }, [activeStore, storesLoading]);

  // Load permissions when active store changes
  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  // Listen for store change events
  useEffect(() => {
    const handleStoreChange = () => {
      // console.log('Store change detected, refreshing permissions...');
      loadPermissions();
    };

    window.addEventListener('storeChanged', handleStoreChange);
    return () => window.removeEventListener('storeChanged', handleStoreChange);
  }, [loadPermissions]);

  const checkPermission = useCallback((permission: keyof UserPermission): boolean => {
    const result = permissions[permission] || false;
    // console.log(`🔍 checkPermission(${permission}):`, result, 'all permissions:', permissions);
    
    // Get user role from active store for debugging
    const activeStoreData = activeStore as any;
    const userRole = activeStoreData?.user_role || 'unknown';
    // console.log(`👤 User role: ${userRole} for store: ${activeStoreData?.shop}`);
    
    // If result is false, show which permission is blocking access
    if (!result && permission) {
      // console.warn(`⚠️ Permission DENIED for "${permission}" → User cannot access this feature`);
      // console.warn(`🔍 Debug info:`, {
      //   permission,
      //   result,
      //   userRole,
      //   permissions,
      //   activeStore: activeStoreData
      // });
    } else {
      // console.log(`✅ Permission GRANTED for "${permission}"`);
    }
    
    return result;
  }, [permissions]);

  const hasAnyPermission = useCallback((requiredPermissions: (keyof UserPermission)[]): boolean => {
    return requiredPermissions.some(perm => permissions[perm] || false);
  }, [permissions]);

  const hasAllPermissions = useCallback((requiredPermissions: (keyof UserPermission)[]): boolean => {
    return requiredPermissions.every(perm => permissions[perm] || false);
  }, [permissions]);

  const refreshPermissions = useCallback(async (): Promise<void> => {
    await loadPermissions();
  }, [loadPermissions]);

  return {
    permissions,
    isLoading,
    error,
    checkPermission,
    hasAnyPermission,
    hasAllPermissions,
    refreshPermissions,
  };
}

// Helper function to get permission config
export function getPermissionConfig(permission: keyof UserPermission) {
  return PERMISSION_CONFIG.find(p => p.id === permission);
}

// Helper function to check if route is accessible
export function isRouteAccessible(route: string, checkPermission: (permission: keyof UserPermission) => boolean): boolean {
  const permission = getPermissionByPage(route);
  if (!permission) return true; // Routes without permission requirements are accessible
  return checkPermission(permission);
}

// Helper function to get permission by page/route
function getPermissionByPage(page: string): keyof UserPermission | null {
  const routeMap: Record<string, keyof UserPermission | null> = {
    '/profile': null, // Profile should be accessible to all authenticated users
    '/sight': 'sight_access',
    '/settings': 'settings_access',
    '/billing': 'billing_access',
    '/invoices': 'billing_access',
    '/credits-history': 'billing_access',
    '/integrations': 'integrations_access',
    '/agency': 'sight_access',
    '/neo-intelligence': 'scaleboard_access',
    '/neo-intelligence/marketing': 'scaleboard_access',
    '/neo-intelligence/product': 'scaleboard_access',
    '/neo-intelligence/customer': 'scaleboard_access',
    '/neo-intelligence/isight-ai': 'scaleboard_access',
    '/support': null, // Hide support menu completely
    '/isights': 'isight_access',
    '/manual-inputs': 'sight_access',
  };

  return routeMap[page] || null;
}
